import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { config } from '../appConfig'
import { CkMinterClient } from '../clients/CkMintClient'
import HyperliquidFundClient from '../clients/HyperliquidFundClient'
import HyperliquidSwapClient from '../clients/HyperliquidSwapClient'
import ICPSwap from '../clients/ICPSwap'
import JupiterClient from '../clients/JupiterClient'
import LiFiClient, { LIFI_COMMISSION } from '../clients/LiFiClient'
import OdinBridgeClient from '../clients/OdinBridgeClient'
import OdinClient, { MIN_ODIN_SWAP_AMOUNT } from '../clients/OdinClient'
import { OneSecBridgeClient } from '../clients/OneSecBridgeClient'
import { solanaClient } from '../clients/SolanaClient'
import Stealthex from '../clients/Stealthex'
import YusanSwapClient from '../clients/YusanSwapClient'
import { SWAP_COMMISSION, SWAP_COMMISSION_MEMO } from '../constants/business'
import { UNKNOWN_ERROR } from '../constants/swapErrors'
import { ICP_ICP_ID, ODINBTC_ID, SOL_ID } from '../constants/tokenIds'
import { Chain, SwapFee } from '../interfaces/common'
import {
  InputError,
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
  SwapStep,
} from '../interfaces/swapInterface'
import { logger } from '../logger'
import { useSwapContext } from '../providers/SwapProvider'
import { useSwapSlippage } from '../state'
import { addMultiplier, maxBigInt } from '../utils/bigint'
import { asICToken, asOdinToken, asSolanaToken } from '../utils/castToken'
import { executeAndSetInterval } from '../utils/executeAndSetInterval'
import { formatMoney, formatNumber } from '../utils/formatNumber'
import { getFeeToken } from '../utils/getFeeToken'
import { transferPlugCommission } from '../utils/transferPlugCommission'
import { decimalsToE8s, e8sToDecimals } from '../utils/validationHelper'
import useBalances from './useBalances'
import useConfig from './useConfig'
import { useTransferFeeEstimate } from './useGas'
import useWallet from './useWallet'

const PRICE_REFRESH_INTERVAL = 15000

export type PriceDifferenceLevel = 'ok' | 'caution' | 'danger'

export interface PriceDifference {
  percentage: number
  color: string
  /** Severity bucket behind `color` and `warn`, for hosts that map it to their own palette. */
  level: PriceDifferenceLevel
  warn: boolean
}

interface Props {
  fromTokenId?: string
  toTokenId?: string
  fromValue?: string
  enabled?: boolean
}

const shouldTakeCommission = (quote: SwapProviderQuote) => {
  // LI.FI & StealthEX handles commission internally
  // Dfinity(Mint CK) - no commision
  return (
    quote.provider !== SwapProvider.LIFI &&
    quote.provider !== SwapProvider.STEALTHEX &&
    quote.provider !== SwapProvider.DFINITY &&
    // HyperCore perp<->spot is an internal transfer of the user's own funds
    quote.provider !== SwapProvider.HYPERLIQUID
  )
}

const useSwap = ({ fromTokenId, toTokenId, fromValue, enabled = true }: Props) => {
  const [currentSwapStep, setCurrentSwapStep] = useState<SwapStep>()
  const [priceLoading, setPriceLoading] = useState(false)
  const [swapExecutionError, setExecutionSwapError] = useState<string>()
  const [solRent, setSolRent] = useState(0n)

  const {
    manualProvider,
    quotes,
    setQuotes,
    quoteFetchErrors,
    setQuoteFetchErrors,
    amount,
    toAmount,
    setSelectedProvider,
    setToAmount,
    setFromToken,
    setToToken,
    fromToken,
    toToken,
    setAmount,
    swapLoading,
  } = useSwapContext()

  const { utxoFee, ethFee, solFee } = useTransferFeeEstimate(fromToken, amount, undefined, toToken)
  const { slippage } = useSwapSlippage()
  const { wallet } = useWallet()
  const { tokens, evmChains, tokenPricesWithChange } = useConfig()
  const { findBalance } = useBalances()
  const { t } = useTranslation()

  const keyringAgent = useMemo(
    () => config.getAgent({ principalId: wallet.principalId }),
    [wallet.principalId]
  )

  const icpSwapService = useMemo(() => new ICPSwap(keyringAgent), [keyringAgent])
  const odinService = useMemo(() => new OdinClient(keyringAgent), [keyringAgent])
  const odinBridgeClient = useMemo(() => new OdinBridgeClient(keyringAgent), [keyringAgent])
  const oneSecBridgeClient = useMemo(() => new OneSecBridgeClient(keyringAgent), [keyringAgent])
  const ckMinterClient = useMemo(() => new CkMinterClient(keyringAgent), [keyringAgent])
  const jupiterService = useMemo(() => new JupiterClient(), [])
  const lifiService = useMemo(() => new LiFiClient(), [])
  const stealthExService = useMemo(() => new Stealthex(), [])
  const yusanSwapService = useMemo(() => new YusanSwapClient(keyringAgent), [keyringAgent])
  const hyperliquidSwapService = useMemo(
    () => new HyperliquidSwapClient(keyringAgent),
    [keyringAgent]
  )
  const hyperliquidFundService = useMemo(
    () => new HyperliquidFundClient(keyringAgent),
    [keyringAgent]
  )

  const currentQuote = useMemo(() => {
    if (manualProvider) return quotes.find((q) => q.provider === manualProvider)

    return quotes.sort((a, b) => b.price - a.price)[0]
  }, [quotes, manualProvider])

  const fromTokenBalance = findBalance(fromToken)?.balance || 0n

  const getTokenById = (id: string | undefined) =>
    id ? tokens.find((token) => token.id === id) : undefined

  useEffect(() => {
    if (!enabled) return

    let fToken = getTokenById(fromTokenId)
    if (!fToken) {
      fToken = getTokenById(ICP_ICP_ID)
    }

    setFromToken(fToken)

    const tToken = getTokenById(toTokenId)
    if (tToken) {
      setToToken(tToken)
    }
  }, [fromTokenId, toTokenId, enabled])

  useEffect(() => {
    if (fromValue) setAmount(fromValue)
  }, [fromValue])

  const getQuotes = useCallback(
    async (amount: string): Promise<(SwapProviderQuote | SwapProviderQuoteFetchError)[]> => {
      const numericAmount = BigInt(decimalsToE8s(amount, fromToken?.decimals || 8))
      const plugCommission = addMultiplier(numericAmount, SWAP_COMMISSION, 'ceil')

      const swapAmount = numericAmount - plugCommission

      if (!fromToken || !toToken || !numericAmount) return []

      const fetches = []

      if (fromToken.chain === Chain.ICP && toToken.chain === Chain.ICP) {
        const from = asICToken(fromToken)
        const to = asICToken(toToken)

        if (icpSwapService) fetches.push(icpSwapService.getQuote(swapAmount, from, to))

        if (YusanSwapClient.isSupported(fromToken) && YusanSwapClient.isSupported(toToken)) {
          fetches.push(yusanSwapService.getQuote(swapAmount, from, to))
        }
      }

      if (fromToken.chain === Chain.ODIN && toToken.chain === Chain.ODIN) {
        const from = asOdinToken(fromToken)
        const to = asOdinToken(toToken)

        fetches.push(odinService.getQuote(swapAmount, from, to))
      }

      if (fromToken.chain === Chain.SOL && toToken.chain === Chain.SOL && wallet.solAddress) {
        const from = asSolanaToken(fromToken)
        const to = asSolanaToken(toToken)

        fetches.push(jupiterService.getQuote(swapAmount, from, to, wallet.solAddress))
      }

      if (fromToken.additionalData.stealthex_symbol && toToken.additionalData.stealthex_symbol) {
        fetches.push(stealthExService.getQuote(swapAmount, fromToken, toToken))
      }

      if (OdinBridgeClient.isSupportedPair(fromToken, toToken)) {
        fetches.push(odinBridgeClient.getQuote(swapAmount, fromToken))
      }

      if (OneSecBridgeClient.isSupportedPair(fromToken, toToken)) {
        fetches.push(oneSecBridgeClient.getQuote(swapAmount, fromToken, toToken, wallet))
      }

      if (CkMinterClient.isSupportedPair(fromToken, toToken)) {
        const price = tokenPricesWithChange[fromToken.id]
        fetches.push(
          ckMinterClient.getQuote(numericAmount, wallet, fromToken, toToken, price.price)
        )
      }

      if (HyperliquidSwapClient.isSupportedPair(fromToken, toToken)) {
        fetches.push(hyperliquidSwapService.getQuote(numericAmount, fromToken, toToken))
      }

      if (HyperliquidFundClient.isSupportedPair(fromToken, toToken)) {
        fetches.push(hyperliquidFundService.getQuote(numericAmount, fromToken, toToken, tokens))
      }

      const liFiSwapAmount = addMultiplier(swapAmount, 1 - LIFI_COMMISSION, 'ceil')
      fetches.push(lifiService.getQuote(liFiSwapAmount, fromToken, toToken, wallet, slippage))

      return Promise.all(fetches)
    },
    [
      fromToken,
      toToken,
      icpSwapService,
      odinService,
      lifiService,
      ckMinterClient,
      hyperliquidFundService,
      hyperliquidSwapService,
      slippage,
      tokens,
    ]
  )

  useEffect(() => {
    if (!enabled || swapLoading) return

    setQuotes([])
    setQuoteFetchErrors([])
    setPriceLoading(true)

    let cancelPriceFetch: Function
    const timeout = setTimeout(() => {
      cancelPriceFetch = executeAndSetInterval(async () => {
        try {
          const quotes = await getQuotes(amount)

          setQuotes(quotes.filter((q): q is SwapProviderQuote => !('error' in q)))
          setQuoteFetchErrors(
            quotes.filter((q) => 'error' in q).sort((e) => (e.error === UNKNOWN_ERROR ? 1 : 0))
          )
        } catch (e) {
          logger.error('Unable to fetch quotes', { originalError: e })
        } finally {
          setPriceLoading(false)
        }
      }, PRICE_REFRESH_INTERVAL)
    }, 1500)

    return () => {
      cancelPriceFetch?.()
      clearTimeout(timeout)
    }
  }, [getQuotes, amount, enabled, swapLoading])

  useEffect(() => {
    setSelectedProvider(currentQuote?.provider)
    setToAmount(formatNumber(currentQuote?.priceHuman, '', { notation: 'standard' }) || '')
  }, [currentQuote, toToken?.decimals])

  useEffect(() => {
    const getRent = async () => {
      if (wallet.solAddress) {
        const rent = await solanaClient.calculateAccountRent(wallet.solAddress)

        setSolRent(rent)
      }
    }

    getRent()
  }, [wallet.solAddress])

  const priceHuman = useMemo(() => {
    if (fromToken && toToken && currentQuote && amount) {
      const price = currentQuote.priceHuman / parseFloat(amount)

      return `1 ${fromToken.symbol} = ${formatNumber(price, toToken.symbol)}`
    }
  }, [fromToken, toToken, currentQuote, amount])

  const performSwap = useCallback(
    async (quote: SwapProviderQuote) => {
      if (!fromToken || !toToken) return Promise.reject('No token selected')

      setCurrentSwapStep(SwapStep.Swap)

      const fromAmount = BigInt(decimalsToE8s(amount, fromToken.decimals))
      const priceLimit = Math.ceil(quote.price * (1 - slippage / 100))
      const plugCommission = addMultiplier(fromAmount, SWAP_COMMISSION)
      const swapAmount = fromAmount - plugCommission

      let receivedAmount
      if (quote.provider === SwapProvider.ICPSWAP && icpSwapService) {
        receivedAmount = await icpSwapService.swap(
          asICToken(fromToken),
          asICToken(toToken),
          swapAmount,
          priceLimit,
          wallet.principalId,
          setCurrentSwapStep
        )
      } else if (quote.provider === SwapProvider.ODIN) {
        if (OdinBridgeClient.isSupportedPair(fromToken, toToken)) {
          receivedAmount = await odinBridgeClient.swap(fromToken, swapAmount, wallet)
        } else {
          receivedAmount = await odinService.swap(
            asOdinToken(fromToken),
            asOdinToken(toToken),
            swapAmount
          )
        }
      } else if (quote.provider === SwapProvider.JUPITER) {
        receivedAmount = await jupiterService.swap(quote, wallet)
      } else if (quote.provider === SwapProvider.LIFI) {
        receivedAmount = await lifiService.swap(quote, wallet, evmChains)
      } else if (quote.provider === SwapProvider.STEALTHEX) {
        receivedAmount = await stealthExService.swap(swapAmount, fromToken, toToken, wallet)
      } else if (quote.provider === SwapProvider.ONESEC) {
        receivedAmount = await oneSecBridgeClient.swap(swapAmount, fromToken, toToken, wallet)
      } else if (quote.provider === SwapProvider.DFINITY) {
        receivedAmount = await ckMinterClient.swap(swapAmount, fromToken, wallet)
      } else if (quote.provider === SwapProvider.YUSAN) {
        receivedAmount = await yusanSwapService.swap(
          asICToken(fromToken),
          asICToken(toToken),
          swapAmount,
          BigInt(priceLimit),
          wallet.principalId,
          setCurrentSwapStep
        )
      } else if (quote.provider === SwapProvider.HYPERLIQUID) {
        if (HyperliquidFundClient.isSupportedPair(fromToken, toToken)) {
          receivedAmount = await hyperliquidFundService.swap(
            fromAmount,
            fromToken,
            wallet,
            tokens,
            slippage,
            setCurrentSwapStep
          )
        } else {
          receivedAmount = await hyperliquidSwapService.swap(
            fromAmount,
            fromToken,
            toToken,
            wallet,
            tokens
          )
        }
      } else {
        throw new Error(`Unrecognized swap quote provider ${quote.provider}`)
      }

      if (shouldTakeCommission(quote)) {
        transferPlugCommission(plugCommission, fromToken, SWAP_COMMISSION_MEMO, wallet)
      }

      return receivedAmount
    },
    [
      icpSwapService,
      odinService,
      jupiterService,
      hyperliquidFundService,
      hyperliquidSwapService,
      lifiService,
      fromToken,
      toToken?.decimals,
      fromToken?.decimals,
      fromToken?.fee,
      toToken?.fee,
      toToken,
      wallet.principalId,
      wallet.solAddress,
      fromToken?.standard,
      slippage,
      amount,
      tokens,
    ]
  )

  const swapFee = useMemo(() => {
    if (!fromToken || !toToken) return

    let swapFee: SwapFee = { items: [] }
    const feeToken = getFeeToken(fromToken, tokens)

    if (currentQuote?.provider === SwapProvider.LIFI) {
      const estimate = currentQuote.metadata!.lifiQuote.estimate
      const gasCosts = estimate?.gasCosts?.[0]?.amount

      if (typeof gasCosts !== 'undefined') {
        swapFee.items.push({
          token: feeToken,
          amount: BigInt(gasCosts),
        })
      }
    }

    if (currentQuote?.provider === SwapProvider.ONESEC) {
      if (currentQuote.metadata?.gasFee) {
        swapFee.items.push({
          token: feeToken,
          amount: currentQuote.metadata!.gasFee,
        })
      }
    }

    if (currentQuote?.provider === SwapProvider.DFINITY && currentQuote?.metadata?.gasFee) {
      const ckmintFeeTokenId = fromToken.additionalData.ckmint_fee_token
      const ckmintFeeToken = tokens.find((t) => t.id === ckmintFeeTokenId)
      swapFee.items.push({
        token: ckmintFeeToken ? ckmintFeeToken : feeToken,
        amount: currentQuote.metadata.gasFee,
      })

      // ck unminting needs 2 fees
      if (fromToken.chain === Chain.ICP) {
        swapFee.items.push({
          token: feeToken,
          amount: fromToken.fee * 2n,
        })
      }
    }

    if (swapFee.items.length === 0) {
      let fallbackAmount: bigint | undefined

      if (fromToken.chain === Chain.ICP) {
        fallbackAmount = fromToken.fee * 2n
      } else if (fromToken.chain === Chain.ODIN) {
        fallbackAmount = fromToken.fee
      } else if (fromToken.chain === Chain.SOL) {
        fallbackAmount = solFee
      } else if (fromToken.evmChain) {
        fallbackAmount = ethFee
      } else if (fromToken.chain === Chain.BTC || fromToken.chain === Chain.DOGE) {
        fallbackAmount = utxoFee
      } else if (fromToken.chain === Chain.HYPERCORE) {
        // HyperCore perp<->spot transfers carry no protocol or gas fee
        fallbackAmount = 0n
      }

      if (typeof fallbackAmount !== 'undefined') {
        swapFee.items.push({
          token: feeToken,
          amount: fallbackAmount,
        })
      }
    }

    return swapFee
  }, [fromToken?.id, toToken?.id, currentQuote, solFee, ethFee, utxoFee, tokens])

  const formattedFee = useMemo(() => {
    if (!swapFee?.items.length) return

    return swapFee.items.map((fee) => {
      const price = tokenPricesWithChange[fee.token.id]
      const amountDec = e8sToDecimals(fee.amount, fee.token.decimals)
      const amountUsd = price ? amountDec * price.price : undefined

      return [
        formatNumber(amountDec, fee.token.symbol),
        formatMoney(amountUsd, undefined, { format: 'precise' }),
      ]
        .filter(Boolean)
        .join(' = ')
    })
  }, [swapFee, tokenPricesWithChange])

  const priceDifference: PriceDifference | undefined = useMemo(() => {
    if (!fromToken || !toToken || !amount || !toAmount) return undefined

    const fromTokenPrice = tokenPricesWithChange[fromToken.id]?.price
    const toTokenPrice = tokenPricesWithChange[toToken.id]?.price

    if (!fromTokenPrice || !toTokenPrice) return undefined

    const fromUsd = fromTokenPrice * Number(amount)
    const toUsd = toTokenPrice * Number(toAmount)

    const percentage = (toUsd - fromUsd) / (fromUsd / 100)

    const level: PriceDifferenceLevel =
      percentage < -4 ? 'danger' : percentage < -2 ? 'caution' : 'ok'
    const color = { ok: '#4CD166', caution: '#FFE700', danger: '#e04949' }[level]

    return {
      percentage,
      color,
      level,
      warn: level === 'danger',
    }
  }, [fromToken, toToken, amount, toAmount, tokenPricesWithChange])

  const maxAmount = useMemo(() => {
    if (!fromToken || !swapFee?.items.length) return fromTokenBalance

    const reservedFeeAmount = swapFee.items
      .filter((item) => item.token.id === fromToken.id)
      .reduce((sum, item) => sum + BigInt(item.amount), 0n)

    let reservedAmount = reservedFeeAmount

    if (fromToken.id === SOL_ID) {
      reservedAmount += solRent
    }

    return maxBigInt(fromTokenBalance - reservedAmount, 0n)
  }, [fromToken?.id, fromTokenBalance, swapFee, solRent])

  const swapError: InputError | undefined = useMemo(() => {
    if (!fromToken || !toToken) {
      return {
        buttonError: t('swap.errors.no_token'),
      }
    }

    const numericAmount = BigInt(decimalsToE8s(amount, fromToken.decimals))

    if (!numericAmount) {
      return {
        buttonError: t('swap.errors.no_amount'),
      }
    }

    if (priceLoading) {
      return {
        buttonError: t('swap.errors.loading_price'),
      }
    }

    if (!currentQuote) {
      let error = t('swap.errors.no_quote')

      const quoteFetchError = quoteFetchErrors[0]
      if (quoteFetchError) {
        if (quoteFetchError.error === UNKNOWN_ERROR) {
          error = 'Failed to fetch quotes'
        } else {
          error = quoteFetchError.error
        }
      }

      return { buttonError: error }
    }

    if (numericAmount > fromTokenBalance) {
      return {
        buttonError: t('swap.errors.not_enough_token', {
          token: fromToken.symbol,
        }),
      }
    }

    if (numericAmount > maxAmount) {
      return {
        buttonError: t('swap.errors.not_enough_token_for_fee', {
          token: fromToken.symbol,
        }),
      }
    }

    const feeItems = swapFee?.items ?? []
    const insufficientFeeItem = feeItems.find((item) => {
      const feeTokenBalance = findBalance(item.token)?.balance || 0n
      return item.amount > feeTokenBalance
    })

    if (insufficientFeeItem) {
      return {
        buttonError: t('swap.errors.not_enough_token_for_fee', {
          token: insufficientFeeItem.token.symbol,
        }),
      }
    }

    if (typeof currentQuote.slippage === 'number' && currentQuote.slippage > slippage) {
      return {
        buttonError: t('swap.errors.slippage_exceeded'),
      }
    }

    if (!feeItems.length) {
      return {
        buttonError: t('swap.errors.no_fee'),
      }
    }

    if (fromToken.chain === Chain.ODIN) {
      const compareAmount = fromToken.id === ODINBTC_ID ? numericAmount : currentQuote.priceHuman

      if (compareAmount < MIN_ODIN_SWAP_AMOUNT) {
        return {
          buttonError: t('swap.errors.less_than_minimal'),
        }
      }
    }
  }, [
    fromToken,
    toToken,
    amount,
    currentQuote,
    priceLoading,
    maxAmount,
    fromTokenBalance,
    slippage,
    quoteFetchErrors,
    swapFee,
  ])

  return {
    performSwap,
    currentSwapStep,
    priceLoading,
    currentQuote,
    swapError,
    swapExecutionError,
    setExecutionSwapError,
    priceHuman,
    getQuotes,
    maxAmount,
    formattedFee,
    priceDifference,
  }
}

export default useSwap
